// The studio's controls, in sections with sensible defaults so it never overwhelms; each has
// "Reset section". Every control writes a typed value into the draft (lib/themes/edit.ts); there
// is no free text anywhere that reaches CSS. Contrast is a warning with a nudge, never a block.
import { useRef, useState, type ReactNode } from 'react'
import { ChevronDown, ImagePlus } from 'lucide-react'
import { api } from '@/runtime'
import { STUDIO_BACKDROPS, type Background, type StudioMode, type ThemeSpec } from '@/kinds/themeprofile/schema'
import { themeSpecToVars } from '@/lib/themeStudio'
import { contrastInputOf, contrastWarnings, fixContrast } from '@/lib/contrast'
import {
  accentOf,
  backgroundOf,
  resetSectionOf,
  setAccent,
  setBackground,
  setSection,
  setSurface,
  setTrueBlack,
  type AccentKey,
  type SectionId,
  type SurfaceKey,
} from '@/lib/themes/edit'
import { downscaleImage } from '@/lib/themes/image'
import { Button, Segmented, cx } from '@/components/ui'

const SURFACES: { key: SurfaceKey; label: string; v: string }[] = [
  { key: 'background', label: 'Background', v: '--color-ink' },
  { key: 'raised', label: 'Raised (cards)', v: '--color-panel' },
  { key: 'sunken', label: 'Sunken', v: '--color-sunken' },
  { key: 'hairline', label: 'Hairline', v: '--color-line' },
  { key: 'text', label: 'Text', v: '--color-fg' },
  { key: 'muted', label: 'Muted text', v: '--color-muted' },
  { key: 'faint', label: 'Faint text', v: '--color-faint' },
]
const ACCENTS: { key: AccentKey; label: string; v: string }[] = [
  { key: 'colour', label: 'Accent', v: '--color-accent' },
  { key: 'highlight', label: 'Highlight', v: '--color-highlight' },
  { key: 'ok', label: 'Ok', v: '--color-ok' },
  { key: 'warn', label: 'Warn', v: '--color-warn' },
  { key: 'danger', label: 'Danger', v: '--color-danger' },
  { key: 'info', label: 'Info', v: '--color-info' },
]

/** `#rrggbb` for an `<input type=color>` from whatever hex the variable holds */
const six = (v: string | undefined, fallback = '#808080') => (v && /^#[0-9a-f]{6}/i.test(v) ? v.slice(0, 7) : fallback)

const Section = ({
  id,
  title,
  blurb,
  open,
  onToggle,
  onReset,
  changed,
  children,
}: {
  id: SectionId
  title: string
  blurb: string
  open: boolean
  onToggle: () => void
  onReset: () => void
  changed: boolean
  children: ReactNode
}) => (
  <section className="border-b border-line last:border-0" data-testid={`studio-section-${id}`}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-center gap-2 py-3 text-left"
    >
      <span className="flex-1">
        <span className="block font-semibold text-[15px]">{title}</span>
        <span className="block text-xs text-muted">{blurb}</span>
      </span>
      {changed && <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-label="changed" />}
      <ChevronDown size={16} className={cx('text-muted transition-transform', open && 'rotate-180')} />
    </button>
    {open && (
      <div className="pb-4 flex flex-col gap-3">
        {children}
        {changed && (
          <div>
            <Button variant="chip" onClick={onReset} data-testid={`studio-reset-${id}`}>
              Reset section
            </Button>
          </div>
        )}
      </div>
    )}
  </section>
)

const Row = ({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) => (
  <label className="flex items-center gap-3 min-h-11">
    <span className="flex-1 min-w-0">
      <span className="block text-sm">{label}</span>
      {hint && <span className="block text-xs text-faint">{hint}</span>}
    </span>
    {children}
  </label>
)

const Colour = ({
  label,
  value,
  set,
  custom,
  id,
}: {
  label: string
  value: string | undefined
  set: (v: string | undefined) => void
  custom: boolean
  id: string
}) => (
  <Row label={label}>
    {custom && (
      <button
        type="button"
        className="text-xs text-muted hover:text-fg"
        onClick={() => set(undefined)}
        aria-label={`Use the default ${label.toLowerCase()}`}
      >
        default
      </button>
    )}
    <input
      type="color"
      value={six(value)}
      onChange={(e) => set(e.target.value)}
      className="h-9 w-11 p-0 border border-line rounded-md bg-transparent"
      data-testid={id}
      aria-label={label}
    />
  </Row>
)

const Slider = ({
  label,
  value,
  min,
  max,
  step,
  set,
  unit = '',
  id,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  set: (v: number) => void
  unit?: string
  id: string
}) => (
  <Row label={label}>
    <input
      type="range"
      className="range w-36"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => set(Number(e.target.value))}
      data-testid={id}
      aria-label={label}
    />
    <span className="w-12 text-right text-xs text-muted tabular">
      {Math.round(value * 100) / 100}
      {unit}
    </span>
  </Row>
)

export const StudioEditor = ({
  spec,
  mode,
  onMode,
  onChange,
}: {
  spec: ThemeSpec
  mode: StudioMode
  onMode: (m: StudioMode) => void
  onChange: (s: ThemeSpec) => void
}) => {
  const [open, setOpen] = useState<SectionId | null>('colours')
  const [busy, setBusy] = useState('')
  const file = useRef<HTMLInputElement>(null)
  const draw = themeSpecToVars(spec, mode)
  const vars = draw.vars
  const input = contrastInputOf(vars)
  const warnings = input ? contrastWarnings(input) : []
  const surfaces = spec.modes?.[mode]?.surfaces ?? {}
  const accent = accentOf(spec, mode) ?? {}
  const bg = backgroundOf(spec, mode)
  const tinted = spec.modes?.black?.trueBlack === false
  const sec = (id: SectionId, title: string, blurb: string, changed: boolean, body: ReactNode) => (
    <Section
      id={id}
      title={title}
      blurb={blurb}
      open={open === id}
      onToggle={() => setOpen(open === id ? null : id)}
      onReset={() => onChange(resetSectionOf(spec, id))}
      changed={changed}
    >
      {body}
    </Section>
  )
  const fix = (id: string, fg: string, bgc: string, need: number) => {
    const next = fixContrast(fg, bgc, need)
    if (id.startsWith('text')) return onChange(setSurface(spec, mode, 'text', next))
    if (id.startsWith('muted')) return onChange(setSurface(spec, mode, 'muted', next))
    if (id.startsWith('faint')) return onChange(setSurface(spec, mode, 'faint', next))
    if (id.startsWith('accent')) return onChange(setAccent(spec, mode, 'colour', next))
    if (id.startsWith('highlight')) return onChange(setAccent(spec, mode, 'highlight', next))
    const k = (['ok', 'warn', 'danger', 'info'] as const).find((x) => id.startsWith(x))
    if (k) onChange(setAccent(spec, mode, k, next))
  }
  const upload = async (f: File | undefined) => {
    if (!f) return
    setBusy('Shrinking…')
    try {
      const small = await downscaleImage(f)
      setBusy('Uploading…')
      const ref = await api.putBlob(small)
      onChange(setBackground(spec, mode, { kind: 'image', blob: ref.id, fit: 'cover', dim: 30, blur: 0 }))
      setBusy('')
    } catch (e) {
      setBusy(`Couldn’t use that picture: ${(e as Error).message}`)
    }
  }
  const kind = bg?.kind ?? 'solid'
  const setKind = (k: Background['kind']) => {
    if (k === 'solid') return onChange(setBackground(spec, mode, undefined))
    if (k === 'gradient')
      return onChange(
        setBackground(spec, mode, {
          kind: 'gradient',
          from: six(vars['--color-ink']),
          to: six(vars['--color-accent']),
          angle: 160,
        }),
      )
    if (k === 'backdrop') return onChange(setBackground(spec, mode, { kind: 'backdrop', id: 'haunt' }))
    file.current?.click()
  }
  return (
    <div data-testid="studio-editor">
      <div className="mb-2">
        <Segmented<StudioMode>
          label="Mode being edited"
          value={mode}
          onChange={onMode}
          options={[
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
            { value: 'black', label: 'Black' },
          ]}
        />
        <p className="text-xs text-faint mt-1.5">
          Colours are set per mode, so one theme answers light, dark and black. The family stays Atkinson
          Hyperlegible Next.
        </p>
      </div>

      {warnings.length > 0 && (
        <div
          className="mb-3 rounded-[var(--radius-control)] border border-line p-3 text-sm"
          role="status"
          data-testid="contrast-warnings"
        >
          <div className="font-semibold" style={{ color: 'var(--color-warn, #f59e0b)' }}>
            Low contrast
          </div>
          <ul className="mt-1 flex flex-col gap-1">
            {warnings.slice(0, 4).map((w) => (
              <li key={w.id} className="flex items-center gap-2">
                <span className="flex-1 text-muted">
                  {w.label}: {w.ratio.toFixed(1)}:1, wants {w.need}:1
                </span>
                <Button variant="chip" onClick={() => fix(w.id, w.fg, w.bg, w.need)}>
                  Fix it
                </Button>
              </li>
            ))}
          </ul>
          <p className="text-xs text-faint mt-1.5">A warning only. You can keep it as it is.</p>
        </div>
      )}

      {sec(
        'colours',
        'Colours',
        'Background, cards, hairlines and text',
        Object.keys(surfaces).length > 0 || tinted,
        <>
          {SURFACES.map((s) => (
            <Colour
              key={s.key}
              id={`studio-${s.key}`}
              label={s.label}
              value={vars[s.v]}
              custom={!!surfaces[s.key]}
              set={(v) => onChange(setSurface(spec, mode, s.key, v))}
            />
          ))}
          {mode === 'black' && (
            <Row label="Let black be tinted" hint="Off keeps true black, whatever background you pick">
              <input
                type="checkbox"
                checked={tinted}
                onChange={(e) => onChange(setTrueBlack(spec, e.target.checked))}
                data-testid="studio-tinted-black"
              />
            </Row>
          )}
        </>,
      )}
      {sec(
        'accent',
        'Accent and states',
        'The accent, a second highlight colour, and ok, warn, danger, info',
        Object.keys(accent).length > 0,
        ACCENTS.map((a) => (
          <Colour
            key={a.key}
            id={`studio-${a.key}`}
            label={a.label}
            value={vars[a.v]}
            custom={!!accent[a.key]}
            set={(v) => onChange(setAccent(spec, mode, a.key, v))}
          />
        )),
      )}
      {sec(
        'background',
        'Background',
        mode === 'black' ? 'For black; light and dark share another' : 'Shared by light and dark',
        !!bg,
        <>
          <Segmented<Background['kind']>
            label="Background kind"
            value={kind}
            onChange={setKind}
            options={[
              { value: 'solid', label: 'Solid' },
              { value: 'gradient', label: 'Gradient' },
              { value: 'image', label: 'Picture' },
              { value: 'backdrop', label: 'Backdrop' },
            ]}
          />
          <input
            ref={file}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid="studio-image-input"
            onChange={(e) => void upload(e.target.files?.[0])}
          />
          {bg?.kind === 'gradient' && (
            <>
              <Colour
                id="studio-grad-from"
                label="From"
                value={bg.from}
                custom={false}
                set={(v) => v && onChange(setBackground(spec, mode, { ...bg, from: v }))}
              />
              <Colour
                id="studio-grad-to"
                label="To"
                value={bg.to}
                custom={false}
                set={(v) => v && onChange(setBackground(spec, mode, { ...bg, to: v }))}
              />
              <Slider
                id="studio-grad-angle"
                label="Angle"
                value={bg.angle ?? 160}
                min={0}
                max={360}
                step={5}
                unit="°"
                set={(v) => onChange(setBackground(spec, mode, { ...bg, angle: v }))}
              />
            </>
          )}
          {bg?.kind === 'image' && (
            <>
              <Slider
                id="studio-img-dim"
                label="Dim"
                value={bg.dim ?? 0}
                min={0}
                max={80}
                step={5}
                unit="%"
                set={(v) => onChange(setBackground(spec, mode, { ...bg, dim: v }))}
              />
              <Slider
                id="studio-img-blur"
                label="Blur"
                value={bg.blur ?? 0}
                min={0}
                max={24}
                step={1}
                unit="px"
                set={(v) => onChange(setBackground(spec, mode, { ...bg, blur: v }))}
              />
              <Button variant="chip" onClick={() => file.current?.click()}>
                <ImagePlus size={14} /> Choose another picture
              </Button>
            </>
          )}
          {bg?.kind === 'backdrop' && (
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Backdrop">
              {STUDIO_BACKDROPS.map((id) => (
                <Button
                  key={id}
                  variant={bg.id === id ? 'chipOn' : 'chip'}
                  role="radio"
                  aria-checked={bg.id === id}
                  onClick={() => onChange(setBackground(spec, mode, { kind: 'backdrop', id }))}
                  data-testid={`studio-backdrop-${id}`}
                >
                  {id === 'haunt' ? 'Haunt 🎃' : id[0]!.toUpperCase() + id.slice(1)}
                </Button>
              ))}
            </div>
          )}
          {kind === 'image' && !bg && <p className="text-xs text-faint">Choose a picture to use…</p>}
          {busy && <p className="text-xs text-muted">{busy}</p>}
          {mode === 'black' && !tinted && bg && (
            <p className="text-xs text-faint">Black keeps its surface true black; the background sits on it.</p>
          )}
        </>,
      )}
      {sec(
        'glass',
        'Glass',
        'Frosted panels over a soft glow, dark and black only',
        !!spec.glass,
        <>
          <Row label="Glass">
            <input
              type="checkbox"
              checked={!!spec.glass?.on}
              onChange={(e) => onChange(setSection(spec, 'glass', { ...spec.glass, on: e.target.checked }))}
              data-testid="studio-glass-on"
            />
          </Row>
          <Slider
            id="studio-glass-strength"
            label="Strength"
            value={spec.glass?.strength ?? 50}
            min={0}
            max={100}
            step={5}
            unit="%"
            set={(v) => onChange(setSection(spec, 'glass', { ...spec.glass, strength: v }))}
          />
          <Slider
            id="studio-glass-blur"
            label="Blur"
            value={spec.glass?.blur ?? 20}
            min={0}
            max={40}
            step={1}
            unit="px"
            set={(v) => onChange(setSection(spec, 'glass', { ...spec.glass, blur: v }))}
          />
        </>,
      )}
      {sec(
        'shape',
        'Shape',
        'Corner radius, border and hairline weight',
        !!spec.shape,
        <>
          <Slider
            id="studio-radius"
            label="Corner radius"
            value={spec.shape?.radius ?? 16}
            min={0}
            max={24}
            step={1}
            unit="px"
            set={(v) => onChange(setSection(spec, 'shape', { ...spec.shape, radius: v }))}
          />
          <Slider
            id="studio-border"
            label="Border"
            value={spec.shape?.border ?? 1}
            min={0}
            max={3}
            step={0.5}
            unit="px"
            set={(v) => onChange(setSection(spec, 'shape', { ...spec.shape, border: v }))}
          />
          <Slider
            id="studio-hairline"
            label="Hairline"
            value={spec.shape?.hairline ?? 1}
            min={0}
            max={3}
            step={0.5}
            unit="px"
            set={(v) => onChange(setSection(spec, 'shape', { ...spec.shape, hairline: v }))}
          />
        </>,
      )}
      {sec(
        'space',
        'Density and spacing',
        'Applies to rows and cards',
        !!spec.space,
        <>
          <Segmented<'compact' | 'cosy' | 'roomy'>
            label="Density"
            value={spec.space?.density ?? 'cosy'}
            onChange={(d) => onChange(setSection(spec, 'space', { ...spec.space, density: d }))}
            options={[
              { value: 'compact', label: 'Compact' },
              { value: 'cosy', label: 'Cosy' },
              { value: 'roomy', label: 'Roomy' },
            ]}
          />
          <Slider
            id="studio-spacing"
            label="Spacing"
            value={spec.space?.spacing ?? 1}
            min={0.85}
            max={1.25}
            step={0.05}
            unit="×"
            set={(v) => onChange(setSection(spec, 'space', { ...spec.space, spacing: v }))}
          />
        </>,
      )}
      {sec(
        'type',
        'Text',
        'Size and weight. The font stays Atkinson Hyperlegible Next',
        !!spec.type,
        <>
          <Slider
            id="studio-type-size"
            label="Size"
            value={spec.type?.size ?? 1}
            min={0.9}
            max={1.2}
            step={0.05}
            unit="×"
            set={(v) => onChange(setSection(spec, 'type', { ...spec.type, size: v }))}
          />
          <Segmented<400 | 500 | 600>
            label="Weight"
            value={spec.type?.weight ?? 400}
            onChange={(w) => onChange(setSection(spec, 'type', { ...spec.type, weight: w }))}
            options={[
              { value: 400, label: 'Regular' },
              { value: 500, label: 'Medium' },
              { value: 600, label: 'Semibold' },
            ]}
          />
        </>,
      )}
      {sec(
        'motion',
        'Motion',
        'Full, reduced or none. The system’s reduce-motion still wins',
        !!spec.motion,
        <Segmented<'full' | 'reduced' | 'none'>
          label="Motion"
          value={spec.motion ?? 'full'}
          onChange={(m) => onChange({ ...spec, motion: m })}
          options={[
            { value: 'full', label: 'Full' },
            { value: 'reduced', label: 'Reduced' },
            { value: 'none', label: 'None' },
          ]}
        />,
      )}
      {sec(
        'icons',
        'App icon tint',
        'A wash of colour over the launcher icons',
        !!spec.icons,
        <>
          <Segmented<'none' | 'accent' | 'highlight'>
            label="Icon tint"
            value={(['none', 'accent', 'highlight'] as const).find((t) => t === spec.icons?.tint) ?? 'none'}
            onChange={(t) => onChange(setSection(spec, 'icons', { ...spec.icons, tint: t }))}
            options={[
              { value: 'none', label: 'None' },
              { value: 'accent', label: 'Accent' },
              { value: 'highlight', label: 'Highlight' },
            ]}
          />
          <Slider
            id="studio-icon-strength"
            label="Strength"
            value={spec.icons?.strength ?? 30}
            min={0}
            max={100}
            step={5}
            unit="%"
            set={(v) => onChange(setSection(spec, 'icons', { ...spec.icons, strength: v }))}
          />
        </>,
      )}
    </div>
  )
}
